export const TERMINAL_MAX_MESSAGE_BYTES = 24 * 1024;
/** Per `session.input`; the web, the API and the daemon all enforce it. */
export const TERMINAL_MAX_ATTACHMENTS_PER_MESSAGE = 5;

export enum TerminalInputKind {
  CLAUDE = 'claude',
  SHELL = 'shell',
}

export enum TerminalSessionStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  WAITING_PERMISSION = 'waiting_permission',
  ENDED = 'ended',
}

export enum TerminalMachineStatus {
  ONLINE = 'online',
  RECONNECTING = 'reconnecting',
  OFFLINE = 'offline',
}

export enum TerminalPermissionDecision {
  ALLOW = 'allow',
  ALLOW_SESSION = 'allow_session',
  DENY = 'deny',
}

export enum TerminalPermissionMode {
  ASK = 'ask',
  ACCEPT_EDITS = 'accept_edits',
  // Equivalent of `claude --dangerously-skip-permissions`: never the default for a new session.
  BYPASS = 'bypass',
}

/** Values accepted by `claude --model`; DEFAULT sends no flag so the CLI picks its own default. */
export enum TerminalClaudeModel {
  DEFAULT = 'default',
  OPUS = 'opus',
  SONNET = 'sonnet',
  HAIKU = 'haiku',
}

export enum TerminalEntryKind {
  PROMPT = 'prompt',
  SHELL = 'shell',
  SHELL_APPEND = 'shell_append',
  TOOL = 'tool',
  ANSWER = 'answer',
  PERMISSION = 'permission',
  NOTICE = 'notice',
  SUGGESTION = 'suggestion',
  AGENT_STEP = 'agent_step',
  QUESTION = 'question',
  SUBAGENT = 'subagent',
}

export enum TerminalOutputStream {
  STDOUT = 'stdout',
  STDERR = 'stderr',
}

export enum TerminalPermissionStatus {
  PENDING = 'pending',
  ALLOWED = 'allowed',
  DENIED = 'denied',
}

export enum TerminalQuestionStatus {
  PENDING = 'pending',
  ANSWERED = 'answered',
  // The turn was interrupted or the session closed before the user answered.
  CANCELED = 'canceled',
}

export enum TerminalSuggestionType {
  MOVE_TO_DOBOX = 'move_to_dobox',
}

export enum TerminalNoticeType {
  MOVED = 'moved',
  ATTACHED = 'attached',
  UNLINKED = 'unlinked',
  ENDED = 'ended',
  CONTINUED_FROM_RUN = 'continued_from_run',
}

export enum TerminalAgentStepStatus {
  RUNNING = 'running',
  DONE = 'done',
  FAILED = 'failed',
}

export enum TerminalClientMessageType {
  SESSION_LIST = 'session.list',
  SESSION_OPEN = 'session.open',
  SESSION_INPUT = 'session.input',
  SESSION_INTERRUPT = 'session.interrupt',
  SESSION_PERMISSION = 'session.permission',
  SESSION_SET_PERMISSION_MODE = 'session.setPermissionMode',
  SESSION_REPLAY = 'session.replay',
  SESSION_CLOSE = 'session.close',
  FOLDERS_LIST = 'folders.list',
  SESSION_ANSWER = 'session.answer',
  FOLDERS_PICK = 'folders.pick',
  SESSION_SET_ADDITIONAL_DIRS = 'session.setAdditionalDirs',
  SESSION_LINK = 'session.link',
  SESSION_UNLINK = 'session.unlink',
  SESSION_DISMISS_SUGGESTION = 'session.dismissSuggestion',
  SESSION_SET_MODEL = 'session.setModel',
}

export enum TerminalLinkMode {
  LOCAL_CLAUDE_FINISHES = 'local_claude_finishes',
  USER_CONTINUES = 'user_continues',
}

export enum MoveToDoBoxMode {
  NEW = 'new',
  ATTACH = 'attach',
}

export enum TerminalTestsStatus {
  PASSED = 'passed',
  FAILED = 'failed',
}

export enum TerminalTaskMatchReason {
  BRANCH = 'branch',
  MENTION = 'mention',
  SIMILAR = 'similar',
}

/** Why "Local Claude finishes it" was refused; sent as `extensions.reason` next to `internalErrorCode: TERMINAL_FINISH_UNAVAILABLE`. */
export enum TerminalFinishUnavailableReason {
  /** The device has no live relay socket. */
  DEVICE_OFFLINE = 'device_offline',
  /** The device has no usable MCP connection (never linked or removed). */
  NO_CONNECTION = 'no_connection',
  /** The daemon predates `continue_terminal_session`. */
  DAEMON_UNSUPPORTED = 'daemon_unsupported',
  /** The daemon does not know this session (restarted or closed). */
  UNKNOWN_SESSION = 'unknown_session',
  /** The session is mid-turn; try again when it is idle. */
  SESSION_BUSY = 'session_busy',
  /** A run is already continuing this session. */
  SESSION_RUN_ACTIVE = 'session_run_active',
  /** The daemon cannot run terminal sessions right now. */
  TERMINAL_UNAVAILABLE = 'terminal_unavailable',
  /** The task already has an active agent run. */
  TASK_RUN_ACTIVE = 'task_run_active',
  /** The dispatch failed for another reason. */
  DISPATCH_FAILED = 'dispatch_failed',
}

/** `errorCode` the daemon returns from `continue_coding_task_in_terminal` / `get_coding_task_resume_command`. */
export enum ContinueInTerminalErrorCode {
  UNKNOWN_JOB = 'UNKNOWN_JOB',
  RUNTIME_UNSUPPORTED = 'RUNTIME_UNSUPPORTED',
  MODE_UNSUPPORTED = 'MODE_UNSUPPORTED',
  JOB_ACTIVE = 'JOB_ACTIVE',
  JOB_CANCELED = 'JOB_CANCELED',
  NO_SESSION = 'NO_SESSION',
  WORKTREE_UNAVAILABLE = 'WORKTREE_UNAVAILABLE',
  BRANCH_BUSY = 'BRANCH_BUSY',
  TOO_MANY_SESSIONS = 'TOO_MANY_SESSIONS',
  TERMINAL_UNAVAILABLE = 'TERMINAL_UNAVAILABLE',
  JOB_HELD_BY_TERMINAL = 'JOB_HELD_BY_TERMINAL',
}

/** Why "Continue in terminal" was refused; sent as `extensions.reason` next to `internalErrorCode: CONTINUE_IN_TERMINAL_UNAVAILABLE`. */
export enum ContinueInTerminalUnavailableReason {
  UNKNOWN_JOB = 'unknown_job',
  RUNTIME_UNSUPPORTED = 'runtime_unsupported',
  MODE_UNSUPPORTED = 'mode_unsupported',
  JOB_ACTIVE = 'job_active',
  JOB_CANCELED = 'job_canceled',
  NO_SESSION = 'no_session',
  WORKTREE_UNAVAILABLE = 'worktree_unavailable',
  BRANCH_BUSY = 'branch_busy',
  TOO_MANY_SESSIONS = 'too_many_sessions',
  TERMINAL_UNAVAILABLE = 'terminal_unavailable',
  JOB_HELD_BY_TERMINAL = 'job_held_by_terminal',
  DEVICE_OFFLINE = 'device_offline',
  NO_CONNECTION = 'no_connection',
  DAEMON_UNSUPPORTED = 'daemon_unsupported',
  DISPATCH_FAILED = 'dispatch_failed',
}

export enum TerminalServerMessageType {
  SESSION_SNAPSHOT = 'session.snapshot',
  SESSION_ENTRY = 'session.entry',
  SESSION_STATUS = 'session.status',
  SESSION_CLOSED = 'session.closed',
  FOLDERS = 'folders',
  FOLDERS_PICKED = 'folders.picked',
  ERROR = 'error',
}

/** `code` of a `type: 'error'` server message. */
export enum TerminalErrorCode {
  INVALID_MESSAGE = 'invalid_message',
  UNKNOWN_SESSION = 'unknown_session',
  FOLDER_NOT_ALLOWED = 'folder_not_allowed',
  TOO_MANY_SESSIONS = 'too_many_sessions',
  UNKNOWN_PERMISSION_REQUEST = 'unknown_permission_request',
  UNKNOWN_QUESTION_REQUEST = 'unknown_question_request',
  COMMAND_NOT_ALLOWED = 'command_not_allowed',
  CLAUDE_FAILED = 'claude_failed',
  FOLDER_PICK_CANCELLED = 'folder_pick_cancelled',
  FOLDER_PICK_IN_PROGRESS = 'folder_pick_in_progress',
  FOLDER_NOT_GIT_REPOSITORY = 'folder_not_git_repository',
  ATTACHMENT_REJECTED = 'attachment_rejected',
  UNKNOWN_SUGGESTION = 'unknown_suggestion',
}

export const TERMINAL_AUTH_FRAME_TYPE = 'auth';

export interface TerminalOutputChunk {
  stream: TerminalOutputStream;
  text: string;
}

/** A file the user attached to a prompt: uploaded by the web, fetched by the daemon from `downloadUrl`
 *  (presigned S3 GET, short TTL). */
export interface TerminalAttachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  downloadUrl: string;
}

/** What a PROMPT entry shows about an attachment; never the URL or the content. */
export interface TerminalAttachmentSummary {
  name: string;
  contentType: string;
  size: number;
}

export interface TerminalPromptEntry {
  kind: TerminalEntryKind.PROMPT;
  id: string;
  text: string;
  mentions?: string[];
  attachments?: TerminalAttachmentSummary[];
  at: string;
  queued?: boolean;
}

export interface TerminalShellEntry {
  kind: TerminalEntryKind.SHELL;
  id: string;
  command: string;
  at: string;
  output: TerminalOutputChunk[];
  exitCode?: number;
  running: boolean;
}

// Append-only continuation of a shell entry: the client concatenates `appendOutput` onto the entry with the same id.
export interface TerminalShellAppendEntry {
  kind: TerminalEntryKind.SHELL_APPEND;
  id: string;
  appendOutput: TerminalOutputChunk[];
  exitCode?: number;
  running?: boolean;
}

export interface TerminalToolEntry {
  kind: TerminalEntryKind.TOOL;
  id: string;
  verb: string;
  target: string;
  result?: string;
  diff?: { add: number; del: number };
  details?: unknown;
  subagentId?: string;
}

export interface TerminalAnswerEntry {
  kind: TerminalEntryKind.ANSWER;
  id: string;
  text: string;
}

export interface TerminalPermissionEntry {
  kind: TerminalEntryKind.PERMISSION;
  id: string;
  command: string;
  reason?: string;
  status: TerminalPermissionStatus;
}

export interface TerminalSuggestionEntry {
  kind: TerminalEntryKind.SUGGESTION;
  id: string;
  type: TerminalSuggestionType;
  dismissed?: boolean;
}

export interface TerminalNoticeEntry {
  kind: TerminalEntryKind.NOTICE;
  id: string;
  type: TerminalNoticeType;
  taskId?: string;
  /** The link mode in effect when this notice was raised; a notice never re-reads the session's current mode. */
  linkMode?: TerminalLinkMode;
  text: string;
}

export interface TerminalAgentStepEntry {
  kind: TerminalEntryKind.AGENT_STEP;
  id: string;
  runId: string;
  title: string;
  status: TerminalAgentStepStatus;
}

/** An Agent/Task tool call Claude delegated to; its own tool calls are TOOL entries carrying `subagentId` = this id. */
export interface TerminalSubagentEntry {
  kind: TerminalEntryKind.SUBAGENT;
  id: string;
  description: string;
  subagentType?: string;
  /** `run_in_background`: the main turn can end while this one still runs. */
  background: boolean;
  status: TerminalAgentStepStatus;
  toolCount: number;
  /** What it is doing right now, as Claude Code reports it (e.g. "Finding *.ts files"). */
  activity?: string;
  /** Final report excerpt once it ends. */
  summary?: string;
}

export interface TerminalQuestionOption {
  label: string;
  description?: string;
}

export interface TerminalQuestion {
  question: string;
  header?: string;
  options: TerminalQuestionOption[];
  multiSelect: boolean;
}

/** Keyed by the question text; a multi-select answer is the list of chosen labels. */
export type TerminalQuestionAnswers = Record<string, string | string[]>;

/** Claude's AskUserQuestion, answered with session.answer using this entry's id as requestId. */
export interface TerminalQuestionEntry {
  kind: TerminalEntryKind.QUESTION;
  id: string;
  questions: TerminalQuestion[];
  status: TerminalQuestionStatus;
  answers?: TerminalQuestionAnswers;
  freeText?: string;
}

export type TerminalEntry =
  | TerminalPromptEntry
  | TerminalShellEntry
  | TerminalShellAppendEntry
  | TerminalToolEntry
  | TerminalAnswerEntry
  | TerminalPermissionEntry
  | TerminalSuggestionEntry
  | TerminalNoticeEntry
  | TerminalAgentStepEntry
  | TerminalQuestionEntry
  | TerminalSubagentEntry;

export interface TerminalMention {
  taskId: string;
  title: string;
  body: string;
}

export interface TerminalFolder {
  path: string;
  name: string;
}

export interface TerminalSessionSummary {
  id: string;
  machineName: string;
  cwd: string;
  folderName: string;
  branch?: string;
  permissionMode: TerminalPermissionMode;
  /** Absent on a daemon that predates model selection; treated as DEFAULT. */
  model?: TerminalClaudeModel;
  /** The id Claude Code reported for the running process (`system/init` `model`), e.g. `claude-opus-5-5`. */
  claudeModel?: string;
  status: TerminalSessionStatus;
  lastSeq: number;
  claudeSessionId?: string;
  /** Allowed folders Claude may also read and edit (`--add-dir`); the shell stays in `cwd`. */
  additionalDirs?: string[];
  /** DoBox task this session feeds; set by `session.link`, cleared by `session.unlink`. */
  linkedTaskId?: string;
  linkMode?: TerminalLinkMode;
  /** Set while Local Claude finishes the task in the background; the session's Claude is that run. */
  activeRunId?: string;
  /** `statusSeq` of the latest `session.status`; a status with a lower value is stale. */
  statusSeq?: number;
  /** Delegation this session continues (an adopted Local Claude run); the Move popover attaches to it. */
  linkedDelegationId?: string;
  /** Local daemon job whose worktree and conversation this session adopted. */
  adoptedJobId?: string;
}

export interface TerminalAuthFrame {
  type: typeof TERMINAL_AUTH_FRAME_TYPE;
  ticket: string;
}

export type TerminalClientMessage =
  | { type: TerminalClientMessageType.SESSION_LIST }
  | {
      type: TerminalClientMessageType.SESSION_OPEN;
      cwd: string;
      permissionMode?: TerminalPermissionMode;
      model?: TerminalClaudeModel;
      additionalDirs?: string[];
    }
  | {
      type: TerminalClientMessageType.SESSION_INPUT;
      sessionId: string;
      kind: TerminalInputKind;
      text: string;
      mentions?: TerminalMention[];
      attachments?: TerminalAttachment[];
    }
  | { type: TerminalClientMessageType.SESSION_INTERRUPT; sessionId: string }
  | {
      type: TerminalClientMessageType.SESSION_PERMISSION;
      sessionId: string;
      requestId: string;
      decision: TerminalPermissionDecision;
    }
  | {
      type: TerminalClientMessageType.SESSION_SET_PERMISSION_MODE;
      sessionId: string;
      mode: TerminalPermissionMode;
    }
  | {
      type: TerminalClientMessageType.SESSION_SET_MODEL;
      sessionId: string;
      model: TerminalClaudeModel;
    }
  | {
      type: TerminalClientMessageType.SESSION_REPLAY;
      sessionId: string;
      fromSeq: number;
    }
  | { type: TerminalClientMessageType.SESSION_CLOSE; sessionId: string }
  | { type: TerminalClientMessageType.FOLDERS_LIST }
  | {
      type: TerminalClientMessageType.SESSION_ANSWER;
      sessionId: string;
      requestId: string;
      answers: TerminalQuestionAnswers;
      freeText?: string;
    }
  | { type: TerminalClientMessageType.FOLDERS_PICK }
  | {
      type: TerminalClientMessageType.SESSION_SET_ADDITIONAL_DIRS;
      sessionId: string;
      dirs: string[];
    }
  | {
      type: TerminalClientMessageType.SESSION_LINK;
      sessionId: string;
      taskId: string;
      mode: TerminalLinkMode;
      runId?: string;
      /** NEW → the log shows a MOVED notice, ATTACH (or absent) → ATTACHED. */
      moveMode?: MoveToDoBoxMode;
    }
  | { type: TerminalClientMessageType.SESSION_UNLINK; sessionId: string }
  | {
      type: TerminalClientMessageType.SESSION_DISMISS_SUGGESTION;
      sessionId: string;
      entryId: string;
    };

export type TerminalServerMessage =
  | {
      type: TerminalServerMessageType.SESSION_SNAPSHOT;
      session: TerminalSessionSummary;
      entries: TerminalEntry[];
    }
  | {
      type: TerminalServerMessageType.SESSION_ENTRY;
      sessionId: string;
      seq: number;
      entry: TerminalEntry;
    }
  | {
      type: TerminalServerMessageType.SESSION_STATUS;
      sessionId: string;
      status: TerminalSessionStatus;
      cwd: string;
      branch?: string;
      permissionMode: TerminalPermissionMode;
      /** Absent on a daemon that predates model selection; treated as DEFAULT. */
      model?: TerminalClaudeModel;
      /** The id Claude Code reported for the running process (`system/init` `model`), e.g. `claude-opus-5-5`. */
      claudeModel?: string;
      additionalDirs?: string[];
      linkedTaskId?: string;
      linkMode?: TerminalLinkMode;
      activeRunId?: string;
      linkedDelegationId?: string;
      adoptedJobId?: string;
      /** Strictly increasing per session. The relay delivers frames out of order (one Lambda per
       *  frame), so a client applies a status only when its statusSeq beats the last one applied. */
      statusSeq?: number;
    }
  | { type: TerminalServerMessageType.SESSION_CLOSED; sessionId: string }
  | { type: TerminalServerMessageType.FOLDERS; folders: TerminalFolder[] }
  | { type: TerminalServerMessageType.FOLDERS_PICKED; folder: TerminalFolder }
  | {
      type: TerminalServerMessageType.ERROR;
      sessionId?: string;
      code: string;
      message: string;
    };

export interface TerminalSummaryContext {
  branch?: string;
  filesChanged: number;
  /** Absent when no test command ran in the session. */
  tests?: TerminalTestsStatus;
}

export interface TerminalSessionSummaryResult {
  title: string;
  /** "What was done", ending with the "Remaining" line. */
  summary: string;
  /** Becomes the Local Claude run instruction. */
  remaining: string;
  /** Subtitle of the "Local Claude finishes it" option. */
  finishDescription: string;
  context: TerminalSummaryContext;
}

export interface TerminalTaskMatch {
  taskId: string;
  taskGroupId: string;
  title: string;
  reason: TerminalTaskMatchReason;
}

export interface MoveToDoBoxDraft {
  mode: MoveToDoBoxMode;
  title: string;
  summary: string;
  remaining?: string;
  /** Task group the new task goes into (new mode). */
  boxId: string;
  /** Task subgroup (the column) of the new task. */
  column?: string;
  /** Existing task to attach to (attach mode). */
  targetTaskId?: string;
  after: TerminalLinkMode;
  attachTranscript: boolean;
  context: TerminalSummaryContext;
  matches: TerminalTaskMatch[];
}

export interface MoveToDoBoxResult {
  /** Opaque handle for `undoTerminalSessionMove`. */
  moveId: string;
  taskId: string;
  taskGroupId: string;
  /** Set in attach mode and when the transcript became a comment. */
  commentId?: string;
  linkMode: TerminalLinkMode;
  /** Set when Local Claude was started on the task. */
  runId?: string;
}
